import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import "./AdminDashboard.css";

const API_URL = process.env.REACT_APP_API_URL || "";

const BINGO_GAMES = [
  { key: "bingo_10", label: "Bingo 5", url: process.env.REACT_APP_BINGO_10_URL || "http://localhost:8001", stake: 5, total: 75, houseCutPct: 20 },
  { key: "bingo_20", label: "Bingo 10", url: process.env.REACT_APP_BINGO_20_URL || "http://localhost:8002", stake: 10, total: 75, houseCutPct: 20 },
  { key: "emoji_5",  label: "Emoji 5",  url: process.env.REACT_APP_BINGO_30_URL || "http://localhost:8003", stake: 5,  total: 34, houseCutPct: 20 },
  { key: "emoji_10", label: "Emoji 3", url: process.env.REACT_APP_BINGO_40_URL || "http://localhost:8004", stake: 3, total: 64, houseCutPct: 20 },
  { key: "keno_5",   label: "Keno 5", url: process.env.REACT_APP_KENO_5_URL || "http://localhost:8006", stake: 5, total: 80, houseCutPct: 10 },
  { key: "keno_10",  label: "Keno 10", url: process.env.REACT_APP_KENO_10_URL || "http://localhost:8007", stake: 10, total: 80, houseCutPct: 10 },
];

const PHASE_COLORS = { selection: "#facc15", drawing: "#22c55e", ended: "#6b7280", idle: "#6b7280", offline: "#ef4444" };

function AdminDashboard({ onLogout }) {
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsError, setStatsError] = useState(false);
  const [bingoStats, setBingoStats] = useState({});
  const [startingGame, setStartingGame] = useState({});
  const adminToken = localStorage.getItem("adminToken");

  const fetchBingoStats = useCallback(async () => {
    const results = {};
    await Promise.all(
      BINGO_GAMES.map(async (g) => {
        try {
          const [r, hc] = await Promise.all([
            axios.get(`${g.url}/api/admin/stats`, { timeout: 4000, headers: { Authorization: `Bearer ${adminToken}` } }),
            axios.get(`${g.url}/api/admin/house-cut`, { timeout: 4000, headers: { Authorization: `Bearer ${adminToken}` } }),
          ]);
          results[g.key] = {
            ...r.data,
            daily_house_cut: hc.data.daily || 0,
            bot_daily_cut: hc.data.bot_daily || 0,
            monthly_house_cut: hc.data.monthly || 0,
            six_month_house_cut: hc.data.six_month || 0,
            bot_monthly_cut: hc.data.bot_monthly || 0,
            bot_6month_cut: hc.data.bot_6month || 0,
          };
        } catch {
          results[g.key] = { phase: "offline", prize_pool: 0, house_cut: 0, winners_share: 0, players: 0, called: 0, daily_house_cut: 0, bot_daily_cut: 0, monthly_house_cut: 0, six_month_house_cut: 0, bot_monthly_cut: 0, bot_6month_cut: 0 };
        }
      })
    );
    setBingoStats(results);
  }, []);

  useEffect(() => {
    fetchStats();
    fetchBingoStats();
    const interval = setInterval(fetchBingoStats, 5000);
    const statsInterval = setInterval(fetchStats, 30000);
    return () => { clearInterval(interval); clearInterval(statsInterval); };
  }, [fetchBingoStats]);

  const fetchStats = async () => {
    setStatsError(false);
    try {
      const response = await axios.get(`${API_URL}/admin/stats`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setStats(response.data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("adminToken");
        onLogout();
        return;
      }
      setStatsError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleStartGame = async (game) => {
    setStartingGame((p) => ({ ...p, [game.key]: true }));
    try {
      await axios.post(`${game.url}/api/admin/start-game`, {}, { timeout: 5000, headers: { Authorization: `Bearer ${adminToken}` } });
      await fetchBingoStats();
    } catch (e) {
      alert(`Failed to start ${game.label}: ${e.message}`);
    } finally {
      setStartingGame((p) => ({ ...p, [game.key]: false }));
    }
  };

  const totalHouseCut = BINGO_GAMES.reduce((s, g) => s + (bingoStats[g.key]?.daily_house_cut || 0), 0);
  const totalBotCut = BINGO_GAMES.reduce((s, g) => s + (bingoStats[g.key]?.bot_daily_cut || 0), 0);
  const netHouseCut = totalHouseCut - totalBotCut;

  const totalMonthlyHouseCut = BINGO_GAMES.reduce((s, g) => s + (bingoStats[g.key]?.monthly_house_cut || 0), 0);
  const totalMonthlyBotCut = BINGO_GAMES.reduce((s, g) => s + (bingoStats[g.key]?.bot_monthly_cut || 0), 0);
  const netMonthly = totalMonthlyHouseCut - totalMonthlyBotCut;

  const total6MonthHouseCut = BINGO_GAMES.reduce((s, g) => s + (bingoStats[g.key]?.six_month_house_cut || 0), 0);
  const total6MonthBotCut = BINGO_GAMES.reduce((s, g) => s + (bingoStats[g.key]?.bot_6month_cut || 0), 0);
  const net6Month = total6MonthHouseCut - total6MonthBotCut;

  const totalBingoPlayers = BINGO_GAMES.reduce((s, g) => s + (bingoStats[g.key]?.players || 0), 0);

  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <h1>Lala Game Admin Dashboard</h1>
        <button onClick={onLogout} className="logout-button">
          Logout
        </button>
      </header>

      <nav className="dashboard-nav">
        <Link to="/dashboard" className={`nav-link${location.pathname==="/dashboard"?" active":""}`}>Dashboard</Link>
        <Link to="/bots" className={`nav-link${location.pathname==="/bots"?" active":""}`}>Bots</Link>
        <Link to="/wallet" className={`nav-link${location.pathname==="/wallet"?" active":""}`}>Wallet</Link>
        <Link to="/house" className={`nav-link${location.pathname==="/house"?" active":""}`}>🏦 House</Link>
        <Link to="/deposits" className={`nav-link${location.pathname==="/deposits"?" active":""}`}>Deposits</Link>
        <Link to="/withdrawals" className={`nav-link${location.pathname==="/withdrawals"?" active":""}`}>Withdrawals</Link>
        <Link to="/users" className={`nav-link${location.pathname==="/users"?" active":""}`}>Users</Link>
        <Link to="/bonus" className={`nav-link${location.pathname==="/bonus"?" active":""}`}>🎁 Bonus</Link>
        <Link to="/promo" className={`nav-link${location.pathname==="/promo"?" active":""}`}>💜 Promo</Link>
      </nav>

      <main className="dashboard-content">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : stats ? (
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Total Users</h3>
              <p className="stat-value">{stats.total_users}</p>
            </div>
            <div className="stat-card">
              <h3>Active Users</h3>
              <p className="stat-value">{stats.active_users}</p>
            </div>
            <div className="stat-card">
              <h3>Balance</h3>
              <p className="stat-value">{stats.total_balance.toFixed(0)}</p>
            </div>
            <div className="stat-card">
              <h3>Transactions</h3>
              <p className="stat-value">{stats.total_transactions}</p>
            </div>
            <div className="stat-card">
              <h3>Deposits</h3>
              <p className="stat-value positive">{stats.total_deposits.toFixed(0)}</p>
            </div>
            <div className="stat-card">
              <h3>Withdrawals</h3>
              <p className="stat-value negative">{stats.total_withdrawals.toFixed(0)}</p>
            </div>
          </div>
        ) : (
          <div className="error">
            Failed to load statistics
            <button
              onClick={fetchStats}
              style={{marginLeft:"12px",padding:"4px 12px",cursor:"pointer",borderRadius:"6px",border:"none",background:"#7c3aed",color:"#fff",fontSize:"0.8rem"}}
            >Retry</button>
          </div>
        )}

        <div className="bingo-section">
          <div className="bingo-section-header">
            <h2 className="bingo-section-title">🎱 Live Bingo Games</h2>
            <div className="bingo-summary">
              <span>Active Players: <strong>{totalBingoPlayers}</strong></span>
              <span style={{marginLeft:"1.5rem"}}>House Cut Today: <strong style={{color:"#f97316"}}>{totalHouseCut} Birr</strong></span>
              <span style={{marginLeft:"1.5rem"}}>Bot Cut: <strong style={{color:"#9ca3af"}}>{totalBotCut} Birr</strong></span>
              <span style={{marginLeft:"1.5rem"}}>Net Today: <strong style={{color:"#22c55e"}}>{netHouseCut} Birr</strong></span>
              <span style={{marginLeft:"1.5rem"}}>Net Monthly: <strong style={{color:"#3b82f6"}}>{netMonthly} Birr</strong></span>
              <span style={{marginLeft:"1.5rem"}}>Net 6-Month: <strong style={{color:"#a855f7"}}>{net6Month} Birr</strong></span>
            </div>
          </div>
          <div className="bingo-grid">
            {BINGO_GAMES.map((g) => {
              const s = bingoStats[g.key] || {};
              const phase = s.phase || "loading";
              const phaseColor = PHASE_COLORS[phase] || "#6b7280";
              const canStart = phase !== "selection" && phase !== "drawing";
              return (
                <div key={g.key} className="bingo-card">
                  <div className="bingo-card-header">
                    <span className="bingo-card-title">{g.label}</span>
                    <span className="bingo-phase-badge" style={{background: phaseColor}}>{phase.toUpperCase()}</span>
                  </div>
                  <div className="bingo-stats">
                    <div className="bingo-stat"><span>Players</span><strong>{s.players ?? 0}</strong></div>
                    <div className="bingo-stat"><span>Active Bots</span><strong style={{color: (s.active_bots ?? 0) > 0 ? "#3b82f6" : "#9ca3af"}}>{s.active_bots ?? 0}</strong></div>
                    <div className="bingo-stat"><span>Called</span><strong>{s.called ?? 0}/{g.total}</strong></div>
                    <div className="bingo-stat"><span>Prize Pool</span><strong>{s.prize_pool ?? 0} Birr</strong></div>
                    <div className="bingo-stat"><span>Winners Share ({100 - (g.houseCutPct || 20)}%)</span><strong style={{color:"#22c55e"}}>{s.winners_share ?? 0} Birr</strong></div>
                    <div className="bingo-stat"><span>House Cut ({g.houseCutPct || 20}%)</span><strong style={{color:"#f97316"}}>{s.house_cut ?? 0} Birr</strong></div>
                    <div className="bingo-stat"><span>Stake</span><strong>{g.stake} Birr</strong></div>
                  </div>
                  {s.winners && s.winners.length > 0 && (
                    <div className="bingo-winners">
                      <div className="bingo-winners-title">🏆 Last Winners</div>
                      {s.winners.map((w, i) => (
                        <div key={i} className="bingo-winner-row">
                          <span>ID: {w.player_id}</span>
                          <span style={{color:"#22c55e"}}>+{w.prize} Birr</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    className="bingo-start-btn"
                    onClick={() => handleStartGame(g)}
                    disabled={!canStart || startingGame[g.key]}
                  >
                    {startingGame[g.key] ? "Starting..." : "Force Start New Game"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}

export default AdminDashboard;
